import React, { createContext, useContext, useMemo, useRef } from 'react';
import type { DynamicEntitiesFetcher } from './api/fetcher';
import { resolveFetcher } from './api/fetcher';

/**
 * Permisos de escritura del usuario sobre el módulo. Cualquier flag ausente se
 * interpreta como permitido, de modo que un anfitrión que no los pase ve la UI
 * completa igual que siempre.
 */
export interface DynamicEntitiesPermissions {
    canCreate?: boolean;
    canUpdate?: boolean;
    canDelete?: boolean;
}

/** Los mismos permisos ya resueltos: sin `undefined`, listos para renderizar. */
export type ResolvedDynamicEntitiesPermissions = Required<DynamicEntitiesPermissions>;

export interface DynamicEntitiesContextType {
    baseUrl: string;
    onError?: (error: Error) => void;
    /**
     * Reemplaza el `fetch` global en TODAS las peticiones del módulo. El
     * anfitrión lo usa para añadir `Authorization`, `X-Company-Id`,
     * `Accept-Language`, tracing o reintentos.
     *
     * Ausente → se usa el `fetch` global (comportamiento previo, sin cambios).
     *
     * Se inyecta el transporte y no un `token` a propósito: el kit no debe saber
     * nada de autenticación, y un callback vivo no caduca a media sesión como sí
     * lo haría una cabecera fotografiada en el mount.
     */
    fetcher?: DynamicEntitiesFetcher;
    /**
     * Oculta las acciones de escritura que el usuario no puede ejecutar. Sin
     * esta prop se muestran todas.
     */
    permissions?: DynamicEntitiesPermissions;
}

export const DynamicEntitiesContext = createContext<DynamicEntitiesContextType | undefined>(undefined);

export interface DynamicEntitiesProviderProps {
    baseUrl: string;
    onError?: (error: Error) => void;
    /** Ver {@link DynamicEntitiesContextType.fetcher}. */
    fetcher?: DynamicEntitiesFetcher;
    /** Ver {@link DynamicEntitiesContextType.permissions}. */
    permissions?: DynamicEntitiesPermissions;
    children: React.ReactNode;
}

export const DynamicEntitiesProvider: React.FC<DynamicEntitiesProviderProps> = ({
    baseUrl,
    onError,
    fetcher,
    permissions,
    children
}) => {
    // El anfitrión casi siempre pasa estas props como literales inline
    // (`fetcher={(url, init) => ...}`, `permissions={{ ... }}`), que cambian de
    // identidad en cada render. Aguas abajo alimentan `useMemo`/`useEffect` que
    // disparan peticiones, así que se estabilizan aquí: sin esto un arrow inline
    // provocaría un bucle de fetch. La ref siempre apunta al último valor, de
    // modo que un token rotado se usa en la siguiente petición.
    const latestRef = useRef({ fetcher, onError });
    latestRef.current = { fetcher, onError };

    const stableFetcher = useMemo<DynamicEntitiesFetcher>(
        () => (input, init) => {
            const current = latestRef.current.fetcher;
            return current ? current(input, init) : fetch(input, init);
        },
        []
    );

    const stableOnError = useMemo<(error: Error) => void>(
        () => (error: Error) => { latestRef.current.onError?.(error); },
        []
    );

    const { canCreate, canUpdate, canDelete } = permissions ?? {};
    const stablePermissions = useMemo<DynamicEntitiesPermissions>(
        () => ({ canCreate, canUpdate, canDelete }),
        [canCreate, canUpdate, canDelete]
    );

    const value = useMemo<DynamicEntitiesContextType>(
        () => ({
            baseUrl,
            onError: stableOnError,
            fetcher: stableFetcher,
            permissions: stablePermissions
        }),
        [baseUrl, stableOnError, stableFetcher, stablePermissions]
    );

    return (
        <DynamicEntitiesContext.Provider value={value}>
            {children}
        </DynamicEntitiesContext.Provider>
    );
};

export const useDynamicEntitiesContext = (): DynamicEntitiesContextType => {
    const context = useContext(DynamicEntitiesContext);
    if (!context) {
        throw new Error('useDynamicEntitiesContext must be used within a DynamicEntitiesProvider');
    }
    return context;
};

export const useOptionalDynamicEntitiesContext = (): DynamicEntitiesContextType | undefined => {
    return useContext(DynamicEntitiesContext);
};

/**
 * Transporte HTTP ya normalizado. Funciona sin provider (cae al `fetch` global)
 * para los componentes que se pueden montar sueltos.
 */
export const useDynamicEntitiesFetcher = (): DynamicEntitiesFetcher => {
    const context = useContext(DynamicEntitiesContext);
    const fetcher = context?.fetcher;
    return useMemo(() => resolveFetcher(fetcher), [fetcher]);
};

/**
 * Permisos resueltos. Sin provider —o sin la prop— devuelve todo en `true`, que
 * es el comportamiento histórico del módulo.
 */
export const useDynamicEntitiesPermissions = (): ResolvedDynamicEntitiesPermissions => {
    const context = useContext(DynamicEntitiesContext);
    const permissions = context?.permissions;

    return useMemo(() => ({
        canCreate: permissions?.canCreate ?? true,
        canUpdate: permissions?.canUpdate ?? true,
        canDelete: permissions?.canDelete ?? true,
    }), [permissions?.canCreate, permissions?.canUpdate, permissions?.canDelete]);
};
